import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FaChevronDown, FaCheck } from 'react-icons/fa';
import { normalizeBusinessSpecifications } from './partnerSignupBusiness';

export default function SpecificationsDropdown({
  id,
  options,
  value,
  onChange,
  hasError = false,
  errorMessage = '',
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const selected = normalizeBusinessSpecifications(value);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
    };
  }, [open]);

  const toggle = (specValue) => {
    const next = selected.includes(specValue)
      ? selected.filter((v) => v !== specValue)
      : [...selected, specValue];
    const updates = { businessSpecifications: next };
    if (!next.includes('other')) {
      updates.businessSpecificationOther = '';
    }
    onChange(updates);
  };

  const summary = selected.length === 0
    ? 'Select categories'
    : selected.length === 1
      ? (options.find((o) => o.value === selected[0])?.label || selected[0])
      : `${selected.length} categories selected`;

  return (
    <>
      <Wrap ref={wrapRef}>
        <Trigger
          type="button"
          id={id}
          aria-expanded={open}
          aria-haspopup="listbox"
          onClick={() => setOpen((prev) => !prev)}
          $hasError={hasError}
          $open={open}
        >
          <TriggerText $placeholder={selected.length === 0}>{summary}</TriggerText>
          <Chevron $open={open} aria-hidden="true" />
        </Trigger>
        {open && (
          <Menu role="listbox" aria-multiselectable="true">
            {options.map((opt) => {
              const checked = selected.includes(opt.value);
              return (
                <Option
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={checked}
                  $active={checked}
                  onClick={() => toggle(opt.value)}
                >
                  <Check $checked={checked}>{checked ? <FaCheck size={10} /> : null}</Check>
                  <span>{opt.label}</span>
                </Option>
              );
            })}
          </Menu>
        )}
      </Wrap>
      {errorMessage ? <Hint $error>{errorMessage}</Hint> : null}
      {!errorMessage && selected.length > 0 ? (
        <Hint>Tap to add or remove</Hint>
      ) : null}
    </>
  );
}

const Wrap = styled.div`
  position: relative;
  width: 100%;
`;

const Trigger = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 44px;
  padding: 11px 40px 11px 12px;
  border: 1px solid ${(p) => (p.$hasError ? 'var(--pp-error-border)' : p.$open ? 'var(--pp-brand)' : 'var(--pp-border)')};
  border-radius: var(--pp-radius-btn);
  font-size: 14px;
  color: var(--pp-text);
  background: var(--pp-surface);
  box-sizing: border-box;
  cursor: pointer;
  text-align: left;
  position: relative;

  &:focus {
    outline: none;
    border-color: ${(p) => (p.$hasError ? 'var(--pp-error-text)' : 'var(--pp-brand)')};
    box-shadow: ${(p) => (p.$hasError ? 'none' : '0 0 0 3px var(--pp-brand-bg)')};
  }
`;

const TriggerText = styled.span`
  display: block;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${(p) => (p.$placeholder ? 'var(--pp-muted)' : 'var(--pp-text)')};
`;

const Chevron = styled(FaChevronDown)`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%) rotate(${(p) => (p.$open ? '180deg' : '0deg')});
  color: var(--pp-muted);
  font-size: 12px;
  transition: transform 0.2s ease;
  pointer-events: none;
`;

const Menu = styled.div`
  position: absolute;
  z-index: 20;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 240px;
  overflow-y: auto;
  background: var(--pp-surface);
  border: 1px solid var(--pp-border);
  border-radius: var(--pp-radius-btn);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  padding: 4px;
`;

const Option = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 10px;
  border: none;
  border-radius: 6px;
  background: ${(p) => (p.$active ? 'var(--pp-brand-bg)' : 'transparent')};
  color: var(--pp-text);
  font-size: 14px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: var(--pp-brand-bg);
  }
`;

const Check = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1.5px solid ${(p) => (p.$checked ? 'var(--pp-brand)' : 'var(--pp-border)')};
  background: ${(p) => (p.$checked ? 'var(--pp-brand)' : 'var(--pp-surface)')};
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Hint = styled.p`
  font-size: 11px;
  color: ${(p) => (p.$error ? 'var(--pp-error-text)' : 'var(--pp-muted)')};
  margin: 4px 0 0;
`;
